import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import Container from 'components/Container';
import ContainerHeader from 'components/ContainerHeader';
import { HelpModals } from 'components/HelpModals';
import { Table } from 'components/Table';
import Text from 'components/Text';
import { EventHeader } from '../../EventsMenu/components/EventHeader';
import { Column } from '../../Header/StyledHeader';

const GET_EVENT_SCAN_LOGS = gql`
  query GetEventScanLogs($event_id: Int!) {
    getEventScanLogs(event_id: $event_id) {
      ticket
      scanners {
        name
        qty
      }
    }
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-content: start;
  max-width: 80vw;
`;

const TicketSection = styled.div`
  margin-bottom: 30px;
`;

const TicketHeader = styled.div`
  background-color: #f5f5f5;
  padding: 15px 20px;
  border: 1px solid #ddd;
  border-bottom: none;
`;

const TableWrapper = styled.div`
  border: 1px solid #ddd;
  border-top: none;
`;

const ScanLogsReport = () => {
  const [scanLogsData, setScanLogsData] = useState();
  const [totalScanned, setTotalScanned] = useState(0);
  const { id } = useParams();

  const location = useLocation();
  const { event } = location.state || {};

  // Use useQuery directly instead of the custom hook
  const { data, loading, error } = useQuery(GET_EVENT_SCAN_LOGS, {
    variables: { event_id: Number(id) },
    skip: !id,
  });

  useEffect(() => {
    if (data?.getEventScanLogs) {
      const scanLogs = data.getEventScanLogs;

      // Add id field to each scanner for Table component
      const scanLogsWithIds = scanLogs.map((ticket) => ({
        ...ticket,
        scanners: ticket.scanners.map((scanner, index) => ({
          ...scanner,
          id: `${ticket.ticket}-${index}`,
        })),
      }));

      const total = scanLogsWithIds.reduce((acc, ticket) => {
        const ticketTotal = ticket.scanners.reduce((ticketAcc, scanner) => {
          return ticketAcc + scanner.qty;
        }, 0);
        return acc + ticketTotal;
      }, 0);

      setScanLogsData(scanLogsWithIds);
      setTotalScanned(total);
    }
  }, [data]);

  if (error) {
    console.warn('GraphQL Error:', error);
    return <div>Error loading scan logs: {error.message}</div>;
  }

  const columns = [
    {
      label: 'Scanner Name',
      key: 'name',
    },
    {
      label: 'Tickets Scanned',
      key: 'qty',
    },
  ];

  function renderRows(scanner) {
    return scanner;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div
        style={{
          justifyContent: 'space-between',
          display: 'flex',
          paddingTop: 20,
          paddingRight: 40,
        }}
      >
        {event && <EventHeader event={event} margin={'0px 40px 0px 40px'} />}
        <Column noBorder>
          <HelpModals />
        </Column>
      </div>
      <Container>
        <ContainerHeader
          borderWidth={'1px'}
          style={{ position: 'relative', width: '97%' }}
        >
          <TitleContainer
            style={{
              flexDirection: 'row',
              width: '95%',
              justifyContent: 'space-between',
            }}
          >
            <FilterContainer>
              <Text
                type="heading"
                color="#3C4144"
                inlineStyle={{ marginRight: 25, whiteSpace: 'nowrap' }}
              >
                Scan Logs Report
              </Text>
            </FilterContainer>
            <FilterContainer>
              <Text
                type="heading"
                as="h3"
                color="#3C4144"
                inlineStyle={{ marginRight: 20, whiteSpace: 'nowrap' }}
              >
                {`Total Scanned: ${totalScanned}`}
              </Text>
            </FilterContainer>
          </TitleContainer>
        </ContainerHeader>
        <div style={{ padding: 20 }}>
          {scanLogsData?.map((ticketData, index) => (
            <TicketSection key={index}>
              <TicketHeader>
                <Text
                  type="heading"
                  as="h4"
                  color="#3C4144"
                  inlineStyle={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 'normal',
                  }}
                >
                  {process.env.PLATFORM === 'tickethoss'
                    ? 'Ticket Name'
                    : 'Pass Name'}
                  : <strong>{ticketData.ticket}</strong>
                </Text>
              </TicketHeader>
              <TableWrapper>
                <Table
                  items={ticketData.scanners}
                  columns={columns}
                  renderRows={renderRows}
                />
              </TableWrapper>
            </TicketSection>
          ))}
        </div>
      </Container>
    </>
  );
};

export default ScanLogsReport;
